/**
 * Soft / hard admin refresh catalog (G5; design cite G14 ∧ v2.3-G7).
 * Port shape from customer `refreshSessionFetch.ts`.
 *
 * Soft-failure TABLE S1–S8+H1 live ownership = `useProactiveSessionRefresh` +
 * `usePeriodicMeValidation` (YAML todo `proactive-refresh` only):
 *
 * | #  | Trigger                   | Soft/hard |
 * |----|--------------------------|-----------|
 * | S1 | NETWORK_ERROR            | Soft — BAN endAdminSession on offline / transport abort |
 * | S2 | REFRESH_COOLDOWN         | Soft      |
 * | S3 | REFRESH_LOCK_TIMEOUT     | Soft      |
 * | S4 | REFRESH_RACE_LOST        | Soft      |
 * | S5 | TOKEN_VERSION_MISMATCH   | Soft definitive (race-recoverable) |
 * | S6 | REFRESH_SKIPPED_PENDING  | Soft      |
 * | S7 | JWT_TENANT_CODE_PENDING  | Soft — re-prompt establish |
 * | S8 | Soft residual bare 401   | Soft      |
 * | H1 | INVALID_REFRESH_TOKEN / REFRESH_TOKEN_MISSING / SESSION_REVOKED / ACCOUNT_LOCKED | Hard → endAdminSession once |
 *
 * Locked classes (plan Soft vs hard catalog): Soft ⊂ isSoftRefreshFailure;
 * Soft definitive = TOKEN_VERSION_MISMATCH via isDefinitiveRefreshRevocation
 * (not hard; not collapsed into Soft/S4); Soft residual = bare 401 (S8).
 */

import { isAccessTokenExpired } from './adminAccessToken.js';

export const REFRESH_SKIPPED_PENDING = 'REFRESH_SKIPPED_PENDING';
export const JWT_TENANT_CODE_PENDING = 'JWT_TENANT_CODE_PENDING';
export const TOKEN_VERSION_MISMATCH = 'TOKEN_VERSION_MISMATCH';
export const REFRESH_RACE_LOST = 'REFRESH_RACE_LOST';

/** Codes that always mean the refresh family is dead (not a multi-tab race). */
const HARD_DEFINITIVE_REFRESH_REVOCATION_CODES = new Set([
  'INVALID_REFRESH_TOKEN',
  /** Access cookie gone and refresh cookie absent/expired — cannot rotate. */
  'REFRESH_TOKEN_MISSING',
  'SESSION_REVOKED',
  'ACCOUNT_LOCKED',
]);

/**
 * TOKEN_VERSION_MISMATCH can be refresh reuse from a peer tab winning the rotate.
 * Soft definitive — do not wipe session / broadcast logout on first sight;
 * do not treat as hard (H1).
 */
export function isHardDefinitiveRefreshRevocation(code: string | undefined): boolean {
  return code !== undefined && HARD_DEFINITIVE_REFRESH_REVOCATION_CODES.has(code);
}

/** Soft definitive (S5) ∨ hard (H1). TVM is definitive but not hard. */
export function isDefinitiveRefreshRevocation(code: string | undefined): boolean {
  return (
    isHardDefinitiveRefreshRevocation(code) || code === TOKEN_VERSION_MISMATCH
  );
}

export function isRefreshSkippedPending(code: string | undefined): boolean {
  return code === REFRESH_SKIPPED_PENDING || code === JWT_TENANT_CODE_PENDING;
}

/** Customer twin name — S6/S7 pending under R1. */
export function isRefreshSkippedUnderR1(code: string | undefined): boolean {
  return isRefreshSkippedPending(code);
}

/**
 * Soft catalog S1–S4 / S6–S7.
 * S5 TOKEN_VERSION_MISMATCH is soft definitive via isDefinitiveRefreshRevocation —
 * intentionally excluded here (hooks soft-stop on explicit TVM / residual 401).
 * S8 bare 401 is status-driven, not a named code.
 */
export function isSoftRefreshFailure(code: string | undefined): boolean {
  return (
    code === 'NETWORK_ERROR' ||
    code === 'REFRESH_COOLDOWN' ||
    code === 'REFRESH_LOCK_TIMEOUT' ||
    code === REFRESH_RACE_LOST ||
    isRefreshSkippedPending(code)
  );
}

/**
 * S6/S7 — soft skip when refresh tenant binding is pending (customer
 * `pendingRefreshSkipResult` twin).
 *
 * **Live producer:** `attemptAdminSessionFromCookies` /
 * `shouldPendingSkipAdminRefresh` (tenant surface + missing URL slug) returns
 * this result — not catalog-only theater.
 *
 * **Call path (re-prompt establish — S7):** soft-stop in
 * `useProactiveSessionRefresh` / `usePeriodicMeValidation` via
 * `isRefreshSkippedUnderR1`, then `rePromptAdminSessionEstablish`
 * (`/dev/login` | `/{tenant}/login` | `/`) — never treat as `SESSION_REVOKED` / H1.
 *
 * Expired access → `JWT_TENANT_CODE_PENDING` (S7); otherwise
 * `REFRESH_SKIPPED_PENDING` (S6).
 */
export function pendingAdminRefreshSkipResult(accessToken: string | null): {
  ok: false;
  status: 0;
  code: typeof JWT_TENANT_CODE_PENDING | typeof REFRESH_SKIPPED_PENDING;
} {
  const expired =
    accessToken !== null &&
    accessToken.length > 0 &&
    isAccessTokenExpired(accessToken);
  return {
    ok: false,
    status: 0,
    code: expired ? JWT_TENANT_CODE_PENDING : REFRESH_SKIPPED_PENDING,
  };
}
